import React from "react";
import { useTranslation } from "react-i18next";
import "../Style/security.css";



const NS = "securityPage";

function Bullets({ items = [] }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="sec-bullets">
      {items.map((b, i) => (
        <li key={i} className="sec-bullets__item">
          <span className="sec-bullet-dot" aria-hidden="true" />
          <span className="sec-bullets__text">{b}</span>
        </li>
      ))}
    </ul>
  );
}

function Section({ id, title, lead, bullets = [], body = [], variant }) {
  return (
    <section id={id} className={`sec-section ${variant ? `sec-section--${variant}` : ""}`}>
      <div className="sec-container">
        <div className="sec-section__header">
          <h2 className="sec-h2">{title}</h2>
          {lead ? <p className="sec-lead">{lead}</p> : null}
        </div>

        <Bullets items={bullets} />

        {Array.isArray(body) && body.length > 0 ? (
          <div className="sec-body">
            {body.map((p, i) => (
              <p key={i} className="sec-p">{p}</p>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function FAQ({ items = [] }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <section id="faq" className="sec-section sec-section--surface">
      <div className="sec-container">
        <div className="sec-section__header">
          <h2 className="sec-h2">Questions fréquentes — Sécurité & confidentialité</h2>
        </div>

        <div className="sec-faq">
          {items.map((it, i) => (
            <details key={i} className="sec-faq__item">
              <summary className="sec-faq__q">{it.q}</summary>
              <div className="sec-faq__a">
                <p className="sec-p">{it.a}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SecurityPage() {
  const { t } = useTranslation(NS);

  // IMPORTANT: root object
  const page = t("", { returnObjects: true });
  // Selon config, t("") peut retourner "" ; fallback robuste :
  const hero = page?.hero || t("hero", { returnObjects: true });
  const seo = page?.seo || t("seo", { returnObjects: true });
  const sections = page?.sections || t("sections", { returnObjects: true });
  const commitments = page?.commitments || t("commitments", { returnObjects: true });
  const faq = page?.faq || t("faq", { returnObjects: true });
  const footer = page?.footer || t("footer", { returnObjects: true });

  const safeSections = Array.isArray(sections) ? sections : [];
  const heroPillars = Array.isArray(hero?.pillars) ? hero.pillars : [];
  const cardItems = Array.isArray(hero?.card?.items) ? hero.card.items : [];
  const faqItems = Array.isArray(faq?.items) ? faq.items : [];
  const commitmentBullets = Array.isArray(commitments?.bullets) ? commitments.bullets : [];

  return (
        <main className="sec-page" aria-label={seo?.ariaLabel || "Sécurité"}>
          <header className="sec-hero">
            <div className="sec-container sec-hero__grid">
              <div className="sec-hero__copy">
                <p className="sec-eyebrow">{hero?.eyebrow}</p>
                <h1 className="sec-h1">{hero?.title}</h1>
                <p className="sec-hero__lead">{hero?.lead}</p>

                <Bullets items={heroPillars} />

                <div className="sec-hero__cta">
                  <a className="sec-btn sec-btn--primary" href={hero?.primaryCta?.href || "#engagements"}>
                    {hero?.primaryCta?.label || "Voir nos engagements"}
                  </a>
                  <a className="sec-btn sec-btn--ghost" href={hero?.secondaryCta?.href || "#faq"}>
                    {hero?.secondaryCta?.label || "Lire la FAQ"}
                  </a>
                </div>

                {hero?.note ? <p className="sec-note">{hero.note}</p> : null}
              </div>

              <aside className="sec-hero__card" aria-label="Résumé sécurité">
                <div className="sec-card">
                  <h3 className="sec-h3">{hero?.card?.title}</h3>
                  <p className="sec-card__p">{hero?.card?.lead}</p>

                  <ul className="sec-card__list">
                    {cardItems.map((it, i) => (
                      <li key={i} className="sec-card__item">
                        <span className="sec-chip">{it.k}</span>
                        <span className="sec-card__text">{it.v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </header>

          {/* NAV */}
          <nav className="sec-subnav" aria-label="Navigation sections sécurité">
            <div className="sec-container">
              <div className="sec-subnav__row">
                {safeSections.map((s) => (
                  <a key={s.id} className="sec-subnav__link" href={`#${s.id}`}>
                    {s.navLabel}
                  </a>
                ))}
                <a className="sec-subnav__link" href="#engagements">Engagements</a>
                <a className="sec-subnav__link" href="#faq">FAQ</a>
              </div>
            </div>
          </nav>

          {/* SECTIONS */}
          {safeSections.map((s) => (
            <Section
              key={s.id}
              id={s.id}
              title={s.title}
              lead={s.lead}
              bullets={s.bullets}
              body={s.body}
            />
          ))}

          {/* COMMITMENTS */}
          <section id="engagements" className="sec-section sec-section--gradient">
            <div className="sec-container">
              <div className="sec-commitments">
                <div className="sec-commitments__copy">
                  <h2 className="sec-h2">{commitments?.title}</h2>
                  <p className="sec-lead">{commitments?.lead}</p>
                </div>
                <ul className="sec-checklist" aria-label="Engagements Life">
                  {commitmentBullets.map((b, i) => (
                    <li key={i} className="sec-checklist__item">
                      <span className="sec-check" aria-hidden="true">✓</span>
                      <span className="sec-checklist__text">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <FAQ items={faqItems} />

          {/* FOOTER */}
          <footer className="sec-footer">
            <div className="sec-container">
              <p className="sec-footer__title">{footer?.title}</p>
              <p className="sec-footer__text">{footer?.text}</p>
            </div>
          </footer>
        </main>
  );
}
